import React from "react";

const stages = [
  {
    title: "Your work.",
    time: "45 minutes",
    focus: "Depth & ownership",
    description: "Explore something you built, improved, or discovered with someone who understands your discipline. Walk us through your contribution, the difficult decisions, and the evidence that it worked. We will also discuss the role and compensation expectations.",
  },
  {
    title: "Work together.",
    time: "90 minutes",
    paid: true,
    focus: "Judgment & verification",
    description: "Work through a prepared problem from your discipline with a future teammate. We provide the environment, tools, and compute. Use AI and documentation, test an idea, and explain what the results tell you.",
  },
  {
    title: "What comes next.",
    time: "45 minutes",
    focus: "A decision on both sides",
    description: "Meet a founder and discuss what you could own, how we work, and the questions still ahead of us. There is dedicated time for your questions about the people, the research, and the company. Decide what we could build together.",
  },
];

const examples = [
  ["Inference & kernels", "Investigate a performance trace and test an optimization against correctness and representative workloads."],
  ["Agents & multi-agents", "Diagnose a failed workflow and demonstrate a reliable recovery in a prepared environment."],
  ["Desktop worker", "Improve an interaction involving permissions, background work, or state recovery."],
  ["Streaming vision", "Investigate a screen sequence and evaluate a change within a latency budget."],
  ["Serving & fleet", "Explore an incident and test a scheduling or recovery improvement."],
  ["Voice & hearing", "Trace a turn-taking failure and evaluate conversational behavior and latency."],
  ["Post-training & distillation", "Examine an evaluation result and design a small experiment to test its explanation."],
];

export default function CareerInterviewProcess() {
  return (
    <section id="interview-process" className="career-interview brand-container" aria-labelledby="career-interview-title">
      <div className="career-interview-heading">
        <div className="career-section-heading">
          <span className="brand-eyebrow">04 / How we interview</span>
          <h2 id="career-interview-title">Get a feel<br />for the work.</h2>
          <p>Explore something you have built, work through a problem with us, and decide what we could build together.</p>
        </div>
        <div className="career-interview-overview">
          <span className="career-interview-total">3<span>hours, typically</span></span>
          <p>Three stages.<br />One paid working session.</p>
        </div>
      </div>
      <div className="career-interview-statement">
        <p className="career-interview-statement-title">Many are called.<br />Few are chosen.</p>
        <p>Here’s how we get to know your work—and how you get to know ours.</p>
      </div>
      <ol className="career-interview-stages" aria-label="Interview stages">
        {stages.map((stage, index) => (
          <li className="career-interview-stage" key={stage.title}>
            <div className="career-interview-stage-meta">
              <span className="career-interview-number" aria-hidden="true">0{index + 1}</span>
              <span>{stage.time}</span>
              {stage.paid && <span className="career-interview-paid">Paid</span>}
            </div>
            <h3>{stage.title}</h3>
            <p>{stage.description}</p>
            <span className="career-interview-focus">{stage.focus}</span>
          </li>
        ))}
      </ol>
      <div className="career-interview-evidence">
        <h3>Good judgment moves<br />with the evidence.</h3>
        <p>During the working session, we introduce a new piece of information. We want to see how you test your assumptions, update your approach, and explain what you now know.</p>
      </div>
      <div className="career-interview-details">
        <details>
          <summary>Preparation, tools & your time<span aria-hidden="true">+</span></summary>
          <div className="career-interview-detail-grid">
            <div><h4>Bring work you already know.</h4><p>Bring material you are authorized to share. Public projects, experiments, papers, or a fresh technical walkthrough are welcome. Required interview work fits within the scheduled sessions.</p></div>
            <div><h4>We provide the setup.</h4><p>We share the format and evaluation criteria beforehand, and supply the tools and compute. AI and documentation are welcome; understanding and checking the output matters.</p></div>
            <div><h4>Your time has value.</h4><p>We agree the working-session fee and payment timing before scheduling. The default is 90 minutes. If a role calls for a longer session, we discuss it as an alternative and agree the duration and fee upfront.</p></div>
          </div>
        </details>
        <details>
          <summary>Scheduling & your current commitments<span aria-hidden="true">+</span></summary>
          <div className="career-interview-detail-grid career-interview-detail-pair">
            <div><h4>Make room for a conversation.</h4><p>Tell us what works around your existing commitments, including any access or accommodation needs. Depending on the exercise and team availability, we can discuss remote participation, split sessions, or evening and weekend options.</p></div>
            <div><h4>Protect the work that is yours.</h4><p>Use your own device or one we provide. Keep employer accounts, equipment, confidential code, and private data out of the process. If an exercise raises a confidentiality or employment concern, tell us so we can agree an alternative. We will not contact your current employer without your permission.</p></div>
          </div>
        </details>
        <details>
          <summary>A problem for your discipline<span aria-hidden="true">+</span></summary>
          <div className="career-interview-detail-body">
            <p>Exercises are prepared for evaluation; we do not use candidates’ submissions in production. Depending on the role, progress might be a tested change, a well-designed experiment, or a diagnosis supported by evidence. Examples include:</p>
            <dl className="career-interview-examples">
              {examples.map(([role, example]) => <div key={role}><dt>{role}</dt><dd>{example}</dd></div>)}
            </dl>
          </div>
        </details>
        <details>
          <summary>What we evaluate & when you will hear back<span aria-hidden="true">+</span></summary>
          <div className="career-interview-detail-grid career-interview-detail-pair">
            <div><h4>A clear, consistent bar.</h4><p>Technical depth, sound judgment, careful verification, learning, and collaboration. Candidates for the same role are assessed against the same criteria. Interviewers record their evidence independently before discussing a decision.</p></div>
            <div><h4>A clear next step.</h4><p>Our target is to complete interviews within five business days of the first conversation, around your availability, and communicate a decision within two business days of the final conversation. If that timing changes, we will let you know.</p></div>
          </div>
        </details>
      </div>
    </section>
  );
}
